import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatMenuModule } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DialogService } from '../../services/dialog.service';
import { FileUploadService } from '../../services/file-upload.service';
import { DataService } from '../../services/data.service';
import { LayoutService } from '../../services/layout.service';
import { AuthService } from '../../services/auth.service';
import { NewProjectDialogComponent, NewProjectData } from '../../components/new-project-dialog/new-project-dialog.component';
import { NewDataSourceDialogComponent, DataSourceFormData } from '../../components/new-data-source-dialog/new-data-source-dialog.component';
import { DataSourcePickerComponent } from '../../components/data-source-picker/data-source-picker.component';
import { ProjectGet } from '../../models/project/project-get.model';
import { ProjectCreate } from '../../models/project/project-create.model';
import { CollectionGet, DataSourceGet } from '../../models';
import { UnifiedActivityItem } from '../../models/project/unified-activity-item.model';
import { CleanStatus, CleanStatusHelper } from '../../models/project/clean-status.enum';
import { Subscription } from 'rxjs';

interface UploadedFileData {
  name: string;
  data: any;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule, 
    MatButtonModule, 
    MatIconModule, 
    MatSnackBarModule,
    MatMenuModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    RouterModule,
    DataSourcePickerComponent
  ],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
  activityItems: UnifiedActivityItem[] = [];
  loading = false;
  error: string | null = null;
  isProjectsScrolled = false;
  isProjectsScrolledToEnd = false;
  dashboardHoverClass = '';
  isRecentActivityExpanded = false;
  isCreating = false;
  creatingMessage = '';
  
  private dashboardResetSubscription?: Subscription;
  
  // Expandable button state
  expandedButton: 'profile-data' | 'anomaly-detection' | 'ai-readiness' | 'clean-data' | 'hypothesis-testing' | null = null;
  profileDataInput: string = '';
  anomalyDetectionInput: string = '';
  hypothesisTestingInput: string = '';
  uploadedFile: UploadedFileData | null = null;
  cleanDataUploadedFile: UploadedFileData | null = null;
  anomalyDetectionUploadedFile: UploadedFileData | null = null;
  hypothesisTestingUploadedFile: UploadedFileData | null = null;
  saveProfile: boolean = true;
  isUploadedFile: boolean = false; // Track if profile data is from uploaded file
  
  // Dropdown state for add button
  showProfileDataSourceMenu = false;
  showCleanDataSourceMenu = false;
  showAnomalyDataSourceMenu = false;
  showHypothesisTestingDataSourceMenu = false;
  showAiReadinessDataSourceMenu = false;
  showProfileDataSourceList = false;
  showCleanDataSourceList = false;
  showAnomalyDataSourceList = false;
  showAiReadinessDataSourceList = false;
  dataSources: DataSourceGet[] = [];
  filteredDataSources: DataSourceGet[] = [];
  selectedProfileDataSource: DataSourceGet | null = null;
  selectedProfileDataSources: DataSourceGet[] = [];
  selectedAiReadinessDataSources: DataSourceGet[] = [];
  selectedCleanDataSource: DataSourceGet | null = null;
  selectedAnomalyDataSource: DataSourceGet | null = null;
  selectedHypothesisTestingDataSource: DataSourceGet | null = null;
  moreOptionsSelected = false;
  
  // Search queries for typeahead filtering
  profileDataSourceSearchQuery = '';
  cleanDataSourceSearchQuery = '';
  anomalyDataSourceSearchQuery = '';
  
  // Loading states for file uploads
  uploadingProfile = false;
  uploadingClean = false;
  uploadingAnomaly = false;
  uploadingHypothesis = false;
  
  // Loading states for datasource selection (after creating new datasource)
  isLoadingProfileDataSource = false;
  isLoadingCleanDataSource = false;
  isLoadingAnomalyDataSource = false;
  isLoadingAiReadinessDataSource = false;
  // Loading state for initial datasource list API call
  isLoadingDataSources = false;
  
  // Track which button should show description (for click persistence)
  showDescriptionFor: 'profile-data' | 'clean-data' | 'hypothesis-testing' | 'anomaly-detection' | 'ai-readiness' | null = null;
  
  // Track whether info panel is expanded (starts collapsed)
  isInfoPanelExpanded = false;
  
  // Track when text is transitioning to hide reflow
  isTextTransitioning = false;

  get filteredActivityItems(): UnifiedActivityItem[] {
    if (!this.expandedButton) {
      return this.activityItems;
    }

    return this.activityItems.filter(item => {
      const type = item.sourceType.toLowerCase();
      
      switch (this.expandedButton) {
        case 'profile-data':
          return type.includes('dataprofile') || type.includes('data profile');
        case 'clean-data':
          return type.includes('project');
        case 'anomaly-detection':
          return type.includes('anomaly') || type.includes('detection');
        default:
          return true;
      }
    });
  }

  get activityHeaderText(): string {
    switch (this.expandedButton) {
      case 'profile-data':
        return 'Recent Assessments';
      case 'clean-data':
        return 'Recent Projects';
      case 'anomaly-detection':
        return 'Recent Detections';
      default:
        return 'Recent Activity';
    }
  }

  constructor(
    private dialogService: DialogService,
    private fileUploadService: FileUploadService,
    private dataService: DataService,
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private layoutService: LayoutService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loadActivityList();
    this.loadDataSources();
    
    // Load recent activity drawer state from user settings
    const userSettings = this.authService.getUserSettings();
    if (userSettings?.dashboardActivityExpanded !== undefined) {
      this.isRecentActivityExpanded = userSettings.dashboardActivityExpanded;
    }
    
    // Check for action query parameter from New menu navigation
    this.route.queryParams.subscribe(params => {
      const action = params['action'];
      if (action) {
        // Auto-expand the corresponding section
        switch (action) {
          case 'profile-data':
            this.onProfileDataClick();
            break;
          case 'clean-data':
            this.onCleanDataClick();
            break;
          case 'anomaly-detection':
            this.onAnomalyDetectionClick();
            break;
          case 'ai-readiness':
            this.onAiReadinessClick();
            break;
          case 'hypothesis-testing':
            this.onHypothesisTestingClick();
            break;
          case 'new-datasource':
            this.openNewDataSourceDialog();
            break;
        }
      }
    });

    // Subscribe to dashboard reset events from Home button
    this.dashboardResetSubscription = this.layoutService.dashboardReset$.subscribe(() => {
      this.collapseButton();
    });
  }

  ngOnDestroy(): void {
    this.dashboardResetSubscription?.unsubscribe();
  }

  getDataSourceIcon(dataSource: DataSourceGet): string {
    // Return icon based on dataSourceTypeId
    switch (dataSource.dataSourceTypeId) {
      case 1: // API Listener
        return 'api';
      case 2: // Database
        return 'storage';
      case 3: // File Upload
        return 'description';
      default:
        return 'description';
    }
  }

  onDashboardClick(event: MouseEvent): void {
    // Check if click is outside action buttons area
    const target = event.target as HTMLElement;
    const isActionButton = target.closest('.action-button, .action-button-container');
    
    if (!isActionButton && this.expandedButton !== null) {
      this.collapseButton();
    }
  }

  private collapseButton(): void {
    this.expandedButton = null;
    this.showDescriptionFor = null;
    this.isInfoPanelExpanded = false;
    // Preserve user inputs - only clear UI state
    this.showProfileDataSourceMenu = false;
    this.showCleanDataSourceMenu = false;
    this.showAnomalyDataSourceMenu = false;
    this.showHypothesisTestingDataSourceMenu = false;
    this.showProfileDataSourceList = false;
    this.showCleanDataSourceList = false;
    this.showAnomalyDataSourceList = false;
    this.showAiReadinessDataSourceMenu = false;
    this.showAiReadinessDataSourceList = false;
  }
  
  toggleInfoPanel(): void {
    // Trigger text fade-out
    this.isTextTransitioning = true;
    
    // Toggle panel state
    this.isInfoPanelExpanded = !this.isInfoPanelExpanded;
    
    // Fade text back in after transition completes (250ms total)
    setTimeout(() => {
      this.isTextTransitioning = false;
    }, 250);
  }

  private loadActivityList(): void {
    this.loading = true;
    this.error = null;

    this.dataService.getProjectActivityList().subscribe({
      next: (items) => {
        this.activityItems = items;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading activity list:', error);
        this.error = 'Failed to load recent activity';
        this.snackBar.open(this.error, '×', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
        this.activityItems = [];
        this.loading = false;
      }
    });
  }

  onProfileDataClick(isIconClick: boolean = false): void {
    if (this.expandedButton === 'profile-data') {
      // If clicking icon of expanded card, collapse it
      if (isIconClick) {
        this.collapseButton();
        return;
      }
      // Otherwise do nothing
      return;
    }
    this.showDescriptionFor = 'profile-data';
    this.expandedButton = 'profile-data';
    this.isInfoPanelExpanded = false; // Start with info panel collapsed
    // Preserve existing inputs when expanding
  }

  onCleanDataClick(isIconClick: boolean = false): void {
    if (this.expandedButton === 'clean-data') {
      // If clicking icon of expanded card, collapse it
      if (isIconClick) {
        this.collapseButton();
        return;
      }
      // Otherwise do nothing
      return;
    }
    this.showDescriptionFor = 'clean-data';
    this.expandedButton = 'clean-data';
    this.isInfoPanelExpanded = false; // Start with info panel collapsed
    // Preserve existing inputs when expanding
  }

  onHypothesisTestingClick(isIconClick: boolean = false): void {
    if (this.expandedButton === 'hypothesis-testing') {
      // If clicking icon of expanded card, collapse it
      if (isIconClick) {
        this.collapseButton();
        return;
      }
      // Otherwise do nothing
      return;
    }
    this.showDescriptionFor = 'hypothesis-testing';
    this.expandedButton = 'hypothesis-testing';
    this.isInfoPanelExpanded = false; // Start with info panel collapsed
    // Preserve existing inputs when expanding
  }

  onAnomalyDetectionClick(isIconClick: boolean = false): void {
    if (this.expandedButton === 'anomaly-detection') {
      // If clicking icon of expanded card, collapse it
      if (isIconClick) {
        this.collapseButton();
        return;
      }
      // Otherwise do nothing
      return;
    }
    this.showDescriptionFor = 'anomaly-detection';
    this.expandedButton = 'anomaly-detection';
    this.isInfoPanelExpanded = false; // Start with info panel collapsed
    // Preserve existing inputs when expanding
  }

  onAiReadinessClick(isIconClick: boolean = false): void {
    if (this.expandedButton === 'ai-readiness') {
      if (isIconClick) {
        this.collapseButton();
      }
      return;
    }
    this.showDescriptionFor = 'ai-readiness';
    this.expandedButton = 'ai-readiness';
    this.isInfoPanelExpanded = false;
  }
  
  isProfileDataValid(): boolean {
    return this.uploadedFile !== null || this.selectedProfileDataSources.length > 0;
  }

  isAiReadinessValid(): boolean {
    return this.selectedAiReadinessDataSources.length > 0;
  }

  get selectedAiReadinessDataSourceIds(): string[] {
    return this.selectedAiReadinessDataSources
      .map(dataSource => dataSource.dataSourceId)
      .filter((id): id is string => !!id);
  }

  removeAiReadinessDataSource(dataSource: DataSourceGet): void {
    this.selectedAiReadinessDataSources = this.selectedAiReadinessDataSources.filter(
      selected => selected.dataSourceId !== dataSource.dataSourceId
    );
  }

  get selectedProfileDataSourceIds(): string[] {
    return this.selectedProfileDataSources
      .map(dataSource => dataSource.dataSourceId)
      .filter((id): id is string => !!id);
  }

  removeProfileDataSource(dataSource: DataSourceGet): void {
    this.selectedProfileDataSources = this.selectedProfileDataSources.filter(
      selected => selected.dataSourceId !== dataSource.dataSourceId
    );
    this.selectedProfileDataSource = this.selectedProfileDataSources.at(-1) || null;
  }

  /**
   * Generates a timestamp in the format: YYYY-MM-DD HH:mm:ss
   */
  private generateTimestamp(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  /**
   * Generates a default name for Anomaly Detection runs
   */
  private generateAnomalyDetectionName(): string {
    let baseName = 'Anomaly Detection';
    
    // Try to use datasource or file name as base
    if (this.selectedAnomalyDataSource?.dataSourceName) {
      baseName = this.selectedAnomalyDataSource.dataSourceName;
    } else if (this.anomalyDetectionUploadedFile?.name) {
      baseName = this.anomalyDetectionUploadedFile.name;
    }
    
    return `${baseName} (${this.generateTimestamp()})`;
  }
  
  isCleanDataValid(): boolean {
    return this.cleanDataUploadedFile !== null || this.moreOptionsSelected;
  }
  
  async onProfileDataGo(): Promise<void> {
    if (!this.isProfileDataValid()) return;
    
    this.isCreating = true;
    this.creatingMessage = 'Preparing your data assessment...';
    
    if (this.selectedProfileDataSources.length > 0) {
      this.router.navigate(['/data/profile'], {
        state: {
          assessmentDataSources: this.selectedProfileDataSources.map(dataSource => ({
            dataSourceId: dataSource.dataSourceId,
            dataSourceName: dataSource.dataSourceName,
            dataSourceTypeId: dataSource.dataSourceTypeId
          }))
        }
      });
    } else {
      this.router.navigate(['/data/profile/0']);
    }
  }

  onAiReadinessGo(): void {
    if (!this.isAiReadinessValid()) return;

    this.router.navigate(['/ai-readiness'], {
      state: {
        dataSources: this.selectedAiReadinessDataSources.map(dataSource => ({
          dataSourceId: dataSource.dataSourceId,
          dataSourceName: dataSource.dataSourceName
        }))
      }
    });
  }
  
  onCleanDataGo(): void {
    const dataSourceId = this.cleanDataUploadedFile?.data?.dataSourceId;
    if (!dataSourceId) return;

    this.isCreating = true;
    this.creatingMessage = 'Creating your project...';

    this.dataService.createProject({
      projectName: `New Workflow (${this.generateTimestamp()})`,
      description: '',
      collectionId: undefined
    }).subscribe({
      next: (projectId: number) => {
        this.dataService.assignDataSourceToProject(projectId.toString(), dataSourceId).subscribe({
          next: () => this.router.navigate(['/project-canvas', projectId]),
          error: (error) => {
            console.error('Error assigning datasource to project:', error);
            this.isCreating = false;
            this.creatingMessage = '';
            this.snackBar.open(
              'Project created but failed to assign datasource.',
              '×',
              { horizontalPosition: 'center', verticalPosition: 'top', panelClass: 'error-snackbar' }
            );
            this.router.navigate(['/project-canvas', projectId]);
          }
        });
      },
      error: (error) => {
        console.error('Error creating project:', error);
        this.isCreating = false;
        this.creatingMessage = '';
        if (error.status !== 402) {
          this.snackBar.open(
            'Failed to create project. Please try again.',
            '×',
            {
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: 'error-snackbar'
            }
          );
        }
      }
    });
  }
  
  async onFileUpload(): Promise<void> {
    this.uploadingProfile = true;
    try {
      const uploadedData = await this.fileUploadService.openFileDialog(['.json', '.csv']);
      
      if (uploadedData) {
        this.isUploadedFile = true; // Mark as uploaded file
        this.uploadedFile = {
          name: uploadedData.fileName,
          data: uploadedData
        };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to process file';
      this.snackBar.open(
        `Error: ${errorMessage}`, 
        '×', 
        {
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: 'error-snackbar'
        }
      );
    } finally {
      this.uploadingProfile = false;
    }
  }
  
  onFileRemove(): void {
    this.uploadedFile = null;
    this.isUploadedFile = false;
  }

  onActivityItemClick(item: UnifiedActivityItem): void {
    const type = item.sourceType.toLowerCase();
    
    if (type.includes('project')) {
      this.router.navigate(['/project-canvas', item.primaryKey]);
    } else if (type.includes('dataprofile') || type.includes('data profile')) {
      this.router.navigate(['/data/profile', item.primaryKey], { queryParams: { view: 'existing', name: item.name } });
    } else if (type.includes('anomaly') || type.includes('detection')) {
      this.router.navigate(['/anomaly-detection/run', item.primaryKey]);
    }
  }

  onViewAllProjects(): void {
    this.router.navigate(['/activity']);
  }

  private async handleInsightsWorkflow(): Promise<void> {
    try {
      // Show loading message
      this.snackBar.open('Please select a JSON or CSV file...', '', {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      });

      // Open file dialog
      const uploadedData = await this.fileUploadService.openFileDialog(['.json', '.csv']);
      
      if (uploadedData) {
        // Show success message
        this.snackBar.open(
          `Successfully loaded ${uploadedData.fileName} (${uploadedData.rowCount} rows)`, 
          '×', 
          {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: 'success-snackbar'
          }
        );

        // Navigate to data profile with ID 0 (representing uploaded data)
        this.router.navigate(['/data/profile/0']);
      }
    } catch (error) {
      // Show error message
      const errorMessage = error instanceof Error ? error.message : 'Failed to process file';
      this.snackBar.open(
        `Error: ${errorMessage}`, 
        '×', 
        {
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: 'error-snackbar'
        }
      );
    }
  }

  private openCreateProjectDialog(): void {
    const dialogRef = this.dialogService.openDialog(NewProjectDialogComponent, {
      width: '600px',
      data: { 
        name: '', 
        description: '', 
        tags: '',
        collection: '' // Let user select collection from dialog
      } as NewProjectData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.data) {
        this.createProject(result.data);
      }
    });
  }

  private createProject(data: NewProjectData): void {
    // Create the project data for the API
    const projectData = {
      projectName: data.name,
      description: data.description || '',
      collectionId: data.collection || undefined
    };

    // Call the API to create the project
    this.dataService.createProject(projectData).subscribe({
      next: (projectId: number) => {
        console.log('Project created successfully with ID:', projectId);
        
        this.snackBar.open(
          `Project "${data.name}" created successfully!`, 
          '×', 
          {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: 'success-snackbar'
          }
        );
        
        // Navigate to the project canvas
        this.router.navigate(['/project-canvas', projectId]);
      },
      error: (error) => {
        console.error('Error creating project:', error);
        this.snackBar.open(
          'Failed to create project. Please try again.', 
          '×', 
          {
            duration: 5000,
            horizontalPosition: 'center',
            verticalPosition: 'top',
            panelClass: 'error-snackbar'
          }
        );
      }
    });
  }

  openNewDataSourceDialog(): void {
    const dialogRef = this.dialogService.openDialog(NewDataSourceDialogComponent, {
      width: '800px',
      maxWidth: '90vw',
      data: { 
        title: 'New Data Source',
        showExistingDataSources: false
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      // Clear the action query param when dialog closes
      this.router.navigate([], {
        queryParams: {},
        replaceUrl: true
      });
      
      if (result && result.data) {
        // Reload data sources after creating a new one
        this.loadDataSources();
      }
    });
  }

  /**
   * Get icon for activity item based on sourceType
   */
  getActivityIcon(item: UnifiedActivityItem): string {
    const type = item.sourceType.toLowerCase();
    
    if (type.includes('project')) {
      return 'ai-scan-grey';
    } else if (type.includes('dataprofile') || type.includes('data profile')) {
      return 'chart-grey';
    } else if (type.includes('anomaly') || type.includes('detection')) {
      return 'radar-ai-grey';
    }
    
    return 'ai-scan-grey'; // Default
  }

  /**
   * Get status label for activity items - always "Unknown" since activity list doesn't have status
   */
  getActivityStatus(item: UnifiedActivityItem): string {
    return 'Unknown';
  }

  /**
   * Get status CSS class for activity items - always unknown class
   */
  getActivityStatusClass(item: UnifiedActivityItem): string {
    return 'status-unknown';
  }

  onProjectsScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const scrollThreshold = 50; // Start/end transitions 50px from the edges
    
    // Show left gradient when scrolled more than threshold from start
    this.isProjectsScrolled = element.scrollLeft > scrollThreshold;
    
    // Hide right gradient when within threshold of the end
    const scrolledToEnd = element.scrollLeft + element.clientWidth >= element.scrollWidth - scrollThreshold;
    this.isProjectsScrolledToEnd = scrolledToEnd;
  }

  onProfileDataHover(): void {
    this.dashboardHoverClass = 'profile-data-hover';
  }

  onCleanDataHover(): void {
    this.dashboardHoverClass = 'clean-data-hover';
  }

  onHypothesisTestingHover(): void {
    this.dashboardHoverClass = 'hypothesis-testing-hover';
  }

  onAnomalyDetectionHover(): void {
    this.dashboardHoverClass = 'anomaly-detection-hover';
  }

  onAiReadinessHover(): void {
    this.dashboardHoverClass = 'ai-readiness-hover';
  }

  onButtonLeave(): void {
    this.dashboardHoverClass = '';
  }
  
  shouldShowDescription(buttonType: 'profile-data' | 'anomaly-detection' | 'ai-readiness' | 'clean-data' | 'hypothesis-testing'): boolean {
    // Only show hover description when button is NOT expanded
    // 1. If no button is expanded and hovering this button, show its description
    // 2. If no button is expanded and this button was clicked (showDescriptionFor), show its description
    
    if (this.expandedButton !== null) {
      return false; // Never show hover description when any button is expanded
    }
    
    if (this.expandedButton === null) {
      const isHovering = this.dashboardHoverClass === `${buttonType}-hover`;
      const wasClicked = this.showDescriptionFor === buttonType;
      return isHovering || wasClicked;
    }
    
    return false; // Don't show if a different button is expanded
  }

  getExpandedDescription(buttonType: 'profile-data' | 'anomaly-detection' | 'ai-readiness' | 'clean-data' | 'hypothesis-testing'): string {
    switch (buttonType) {
      case 'profile-data':
        return 'Data Assessment provides comprehensive insights into your data\'s structure, quality, and characteristics. It automatically analyses your datasets to help you understand patterns, identify issues, and make informed decisions about data quality. You can also generate a cleaning workflow based on identified issues.';
      case 'clean-data':
        return 'Clean Data is your central workspace for improving your data quality. With a few simple clicks, create, configure, and manage end-to-end data cleaning workflows that ensures your data is at premium quality. These workflows are repeatable (i.e. for fresh data) and backed by both solid data principles and AI.';
      case 'anomaly-detection':
        return 'Anomaly Detection uses advanced statistical, machine learning, and AI algorithms to identify unusual patterns, outliers, and anomalies in your data. Choose from multiple detection methods optimised for different data types and complexity levels to uncover hidden quality issues.';
      case 'ai-readiness':
        return 'AI Readiness assesses whether your data is suitable for AI and machine learning. Configure the time field and relevant columns to receive headline scores, evidence, and practical next steps for improving model readiness.';
      case 'hypothesis-testing':
        return 'Hypothesis Testing enables you to validate statistical theories and test the significance of relationships in your data. Upload your dataset to perform various statistical tests and determine whether your hypotheses are supported by the data.';
      default:
        return '';
    }
  }

  getHoverDescription(buttonType: 'profile-data' | 'anomaly-detection' | 'ai-readiness' | 'clean-data' | 'hypothesis-testing'): string {
    switch (buttonType) {
      case 'profile-data':
        return 'Quickly identify issues within your data and data structures';
      case 'clean-data':
        return 'Create repeatable data cleaning workflows to fix quality issues';
      case 'anomaly-detection':
        return 'Use advanced algorithms to detect outliers, unusual patterns, and data anomalies across your datasets';
      case 'ai-readiness':
        return 'Measure how ready your data is for AI and machine learning';
      case 'hypothesis-testing':
        return 'Validate theories and test statistical significance';
      default:
        return '';
    }
  }
  
  getPageTitle(): string {
    if (this.expandedButton === null) {
      return 'What would you like to do today?';
    }
    
    switch (this.expandedButton) {
      case 'profile-data':
        return 'Data Assessment';
      case 'clean-data':
        return 'Clean Your Data';
      case 'hypothesis-testing':
        return 'Hypothesis Testing';
      case 'anomaly-detection':
        return 'Detect Anomalies';
      case 'ai-readiness':
        return 'AI Readiness';
      default:
        return 'What would you like to do today?';
    }
  }
  
  getPageIcon(): string | null {
    if (this.expandedButton === null) {
      return null;
    }
    
    switch (this.expandedButton) {
      case 'profile-data':
        return 'chart-blue';
      case 'clean-data':
        return 'ai-scan-blue';
      case 'hypothesis-testing':
        return 'ai-brain-idea-blue';
      case 'anomaly-detection':
        return 'radar-ai-blue';
      case 'ai-readiness':
        return 'ai-brain-idea-blue';
      default:
        return null;
    }
  }
  
  private loadDataSources(): void {
    this.isLoadingDataSources = true;
    this.dataService.getDataSourceList().subscribe({
      next: (dataSources) => {
        this.dataSources = dataSources || [];
        this.isLoadingDataSources = false;
        // If a datasource list view is already open, refresh its base list so the UI updates
        this.refreshFilteredDataSources();
      },
      error: (error) => {
        console.error('Error loading data sources:', error);
        this.dataSources = [];
        this.isLoadingDataSources = false;
        this.refreshFilteredDataSources();
      }
    });
  }

  /**
   * Refresh the base filteredDataSources when the full dataSources array changes.
   * This ensures any open dropdown list reflects newly-loaded data without user action.
   */
  private refreshFilteredDataSources(): void {
    if (this.showProfileDataSourceList) {
      // For profile, exclude API listeners (dataSourceTypeId === 1)
      this.filteredDataSources = this.dataSources.filter(ds => ds.dataSourceTypeId !== 1);
    } else if (this.showCleanDataSourceList) {
      // For clean data, show all datasources
      this.filteredDataSources = this.dataSources;
    } else if (this.showAnomalyDataSourceList) {
      // For anomaly detection, exclude API listeners
      this.filteredDataSources = this.dataSources.filter(ds => ds.dataSourceTypeId !== 1);
    } else if (this.showAiReadinessDataSourceList) {
      // AI readiness accepts the same datasource types as anomaly detection
      this.filteredDataSources = this.dataSources.filter(ds => ds.dataSourceTypeId !== 1);
    }
  }
  
  /**
   * Load data sources and attempt to select a specific one, with retry logic
   * for handling timing issues where the datasource was just created
   */
  private loadDataSourcesAndSelect(
    dataSourceId: string, 
    dataSourceName: string, 
    type: 'profile' | 'clean' | 'anomaly' | 'ai',
    attemptNumber: number
  ): void {
    const maxAttempts = 3;
    const retryDelay = 500; // ms to wait before retrying
    
    this.dataService.getDataSourceList().subscribe({
      next: (dataSources) => {
        this.dataSources = dataSources || [];
        
        // Try to find the newly created datasource (case-insensitive)
        const createdDataSource = this.dataSources.find(ds => 
          ds.dataSourceId?.toLowerCase() === dataSourceId.toLowerCase()
        );
        
        if (createdDataSource) {
          console.log('Auto-selecting newly created datasource:', createdDataSource);
          this.onDataSourceSelect(createdDataSource, type);
          this.clearLoadingState(type);
        } else if (attemptNumber < maxAttempts) {
          // Datasource not found yet, retry after a delay
          console.log(`Datasource not found (attempt ${attemptNumber + 1}/${maxAttempts}), retrying...`);
          setTimeout(() => {
            this.loadDataSourcesAndSelect(dataSourceId, dataSourceName, type, attemptNumber + 1);
          }, retryDelay * (attemptNumber + 1)); // Exponential backoff
        } else {
          // All retries exhausted, create a fallback selection
          console.warn('Created datasource not found after all retries. Creating fallback.');
          console.warn('Available datasources:', this.dataSources.map(ds => ({ id: ds.dataSourceId, name: ds.dataSourceName })));
          
          // Create a minimal DataSourceGet object for selection as fallback
          const fallbackDataSource: DataSourceGet = {
            dataSourceId: dataSourceId,
            dataSourceName: dataSourceName,
            dataSourceTypeId: 3, // Assume file upload
            companyId: '',
            schemaJson: '',
            createdAt: new Date(),
            updatedAt: new Date()
          };
          
          this.onDataSourceSelect(fallbackDataSource, type);
          this.clearLoadingState(type);
        }
      },
      error: (error) => {
        console.error('Error loading data sources:', error);
        this.dataSources = [];
        this.clearLoadingState(type);
      }
    });
  }
  
  /**
   * Clear the loading state for a specific datasource type
   */
  private clearLoadingState(type: 'profile' | 'clean' | 'anomaly' | 'ai'): void {
    console.log('Clearing loading state for type:', type);
    if (type === 'profile') {
      this.isLoadingProfileDataSource = false;
    } else if (type === 'clean') {
      this.isLoadingCleanDataSource = false;
    } else if (type === 'anomaly') {
      this.isLoadingAnomalyDataSource = false;
    } else {
      this.isLoadingAiReadinessDataSource = false;
    }
  }
  
  onAddButtonClick(type: 'profile' | 'clean' | 'anomaly' | 'hypothesis', event: MouseEvent): void {
    event.stopPropagation();
    if (type === 'profile') {
      this.showProfileDataSourceMenu = !this.showProfileDataSourceMenu;
      this.showCleanDataSourceMenu = false;
      this.showAnomalyDataSourceMenu = false;
      this.showHypothesisTestingDataSourceMenu = false;
    } else if (type === 'clean') {
      this.showCleanDataSourceMenu = !this.showCleanDataSourceMenu;
      this.showProfileDataSourceMenu = false;
      this.showAnomalyDataSourceMenu = false;
      this.showHypothesisTestingDataSourceMenu = false;
    } else if (type === 'anomaly') {
      this.showAnomalyDataSourceMenu = !this.showAnomalyDataSourceMenu;
      this.showProfileDataSourceMenu = false;
      this.showCleanDataSourceMenu = false;
      this.showHypothesisTestingDataSourceMenu = false;
    } else {
      this.showHypothesisTestingDataSourceMenu = !this.showHypothesisTestingDataSourceMenu;
      this.showProfileDataSourceMenu = false;
      this.showCleanDataSourceMenu = false;
      this.showAnomalyDataSourceMenu = false;
    }
  }
  
  onUploadFileClick(type: 'profile' | 'clean' | 'anomaly' | 'hypothesis'): void {
    this.showProfileDataSourceMenu = false;
    this.showCleanDataSourceMenu = false;
    this.showAnomalyDataSourceMenu = false;
    this.showHypothesisTestingDataSourceMenu = false;
    this.showProfileDataSourceList = false;
    this.showCleanDataSourceList = false;
    this.showAnomalyDataSourceList = false;
    this.moreOptionsSelected = false; // Clear More Options flag when uploading file
    if (type === 'profile') {
      this.onFileUpload();
    } else if (type === 'clean') {
      this.onCleanDataFileUpload();
    } else if (type === 'anomaly') {
      this.onAnomalyFileUpload();
    } else {
      this.onHypothesisTestingFileUpload();
    }
  }
  
  onSelectExistingClick(type: 'profile' | 'clean' | 'anomaly' | 'ai'): void {
    // Reset search query when opening the list
    if (type === 'profile') {
      this.profileDataSourceSearchQuery = '';
      // For profile, exclude API listeners (dataSourceTypeId === 1)
      this.filteredDataSources = this.dataSources.filter(ds => ds.dataSourceTypeId !== 1);
      this.showProfileDataSourceMenu = false;
      this.showProfileDataSourceList = true;
    } else if (type === 'clean') {
      this.cleanDataSourceSearchQuery = '';
      // For clean data, show all datasources
      this.filteredDataSources = this.dataSources;
      this.showCleanDataSourceMenu = false;
      this.showCleanDataSourceList = true;
    } else if (type === 'anomaly') {
      this.anomalyDataSourceSearchQuery = '';
      // For anomaly detection, exclude API listeners (dataSourceTypeId === 1)
      this.filteredDataSources = this.dataSources.filter(ds => ds.dataSourceTypeId !== 1);
      this.showAnomalyDataSourceMenu = false;
      this.showAnomalyDataSourceList = true;
    } else {
      this.filteredDataSources = this.dataSources.filter(ds => ds.dataSourceTypeId !== 1);
      this.showAiReadinessDataSourceMenu = false;
      this.showAiReadinessDataSourceList = true;
    }
  }

  onCreateNewDatasourceClick(type: 'profile' | 'clean' | 'anomaly' | 'ai'): void {
    // Close all menus
    this.showProfileDataSourceMenu = false;
    this.showCleanDataSourceMenu = false;
    this.showAnomalyDataSourceMenu = false;
    this.showProfileDataSourceList = false;
    this.showCleanDataSourceList = false;
    this.showAnomalyDataSourceList = false;
    this.showAiReadinessDataSourceList = false;
    
    // Set loading state based on type
    if (type === 'profile') {
      this.isLoadingProfileDataSource = true;
      console.log('Set isLoadingProfileDataSource = true');
    } else if (type === 'clean') {
      this.isLoadingCleanDataSource = true;
      console.log('Set isLoadingCleanDataSource = true');
    } else if (type === 'anomaly') {
      this.isLoadingAnomalyDataSource = true;
      console.log('Set isLoadingAnomalyDataSource = true');
    }
    
    // Determine feature context based on type
    let featureContext: 'data-profile' | 'anomaly-detection' | 'general' = 'general';
    if (type === 'profile') {
      featureContext = 'data-profile';
    } else if (type === 'anomaly') {
      featureContext = 'anomaly-detection';
    }
    
    // Open the New Datasource dialog
    const dialogRef = this.dialogService.openDialog(NewDataSourceDialogComponent, {
      width: '800px',
      maxWidth: '90vw',
      data: { 
        title: 'New Data Source',
        showExistingDataSources: false,
        featureContext: featureContext
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.data) {
        // Extract the created datasource details
        const createdDataSourceId = result.data.dataSourceId;
        const createdDataSourceName = result.data.name || result.data.dataSourceName;
        
        console.log('Datasource created from dialog:', { createdDataSourceId, createdDataSourceName, type });
        
        // Add a small delay to ensure the datasource is indexed before querying
        // This helps avoid timing issues where the datasource was just created
        setTimeout(() => {
          this.loadDataSourcesAndSelect(createdDataSourceId, createdDataSourceName, type, 0);
        }, 300);
      } else {
        // User cancelled, clear loading state
        this.clearLoadingState(type);
      }
    });
  }

  onBackToMainMenu(type: 'profile' | 'clean' | 'anomaly'): void {
    if (type === 'profile') {
      this.showProfileDataSourceList = false;
      this.showProfileDataSourceMenu = true;
      this.filteredDataSources = [];
      this.profileDataSourceSearchQuery = '';
    } else if (type === 'clean') {
      this.showCleanDataSourceList = false;
      this.showCleanDataSourceMenu = true;
      this.filteredDataSources = [];
      this.cleanDataSourceSearchQuery = '';
    } else {
      this.showAnomalyDataSourceList = false;
      this.showAnomalyDataSourceMenu = true;
      this.filteredDataSources = [];
      this.anomalyDataSourceSearchQuery = '';
    }
  }
  
  onDataSourceSelect(dataSource: DataSourceGet, type: 'profile' | 'clean' | 'anomaly' | 'ai'): void {
    if (type === 'profile') {
      if (this.selectedProfileDataSources.some(selected => selected.dataSourceId === dataSource.dataSourceId)) {
        return;
      }

      this.selectedProfileDataSources = [...this.selectedProfileDataSources, dataSource];
      this.selectedProfileDataSource = dataSource;
      this.saveProfile = true; // Always save profile for datasource selections
      this.isUploadedFile = false; // This is a selected datasource, not an uploaded file
      this.profileDataInput = dataSource.dataSourceName || '';
      // Close menus after selection
      this.showProfileDataSourceList = false;
      this.showProfileDataSourceMenu = false;
    } else if (type === 'clean') {
      this.selectedCleanDataSource = dataSource;
      this.moreOptionsSelected = false; // Clear More Options flag when datasource is selected
      // Ensure dataSourceId is extracted as a string
      const dataSourceId = typeof dataSource.dataSourceId === 'string' 
        ? dataSource.dataSourceId 
        : (dataSource.dataSourceId as any)?.dataSourceId || String(dataSource.dataSourceId);
      this.cleanDataUploadedFile = {
        name: dataSource.dataSourceName || 'Selected Data Source',
        data: {
          dataSourceId: dataSourceId,
          dataSourceName: dataSource.dataSourceName
        }
      };
      // Close menus after selection
      this.showCleanDataSourceList = false;
      this.showCleanDataSourceMenu = false;
    } else if (type === 'anomaly') {
      this.selectedAnomalyDataSource = dataSource;
      // Ensure dataSourceId is extracted as a string
      const dataSourceId = typeof dataSource.dataSourceId === 'string' 
        ? dataSource.dataSourceId 
        : (dataSource.dataSourceId as any)?.dataSourceId || String(dataSource.dataSourceId);
      this.anomalyDetectionUploadedFile = {
        name: dataSource.dataSourceName || 'Selected Data Source',
        data: {
          dataSourceId: dataSourceId,
          dataSourceName: dataSource.dataSourceName
        }
      };
      this.anomalyDetectionInput = dataSource.dataSourceName || '';
      // Close menus after selection
      this.showAnomalyDataSourceList = false;
      this.showAnomalyDataSourceMenu = false;
    } else {
      if (this.selectedAiReadinessDataSources.some(selected => selected.dataSourceId === dataSource.dataSourceId)) {
        return;
      }
      this.selectedAiReadinessDataSources = [...this.selectedAiReadinessDataSources, dataSource];
      this.showAiReadinessDataSourceList = false;
      this.showAiReadinessDataSourceMenu = false;
    }
  }
  
  /**
   * Get filtered datasources for Profile Data based on search query
   */
  get filteredProfileDataSources(): DataSourceGet[] {
    if (!this.profileDataSourceSearchQuery.trim()) {
      return this.filteredDataSources;
    }
    const query = this.profileDataSourceSearchQuery.toLowerCase();
    return this.filteredDataSources.filter(ds => 
      ds.dataSourceName?.toLowerCase().includes(query)
    );
  }
  
  /**
   * Get filtered datasources for Clean Data based on search query
   */
  get filteredCleanDataSources(): DataSourceGet[] {
    if (!this.cleanDataSourceSearchQuery.trim()) {
      return this.filteredDataSources;
    }
    const query = this.cleanDataSourceSearchQuery.toLowerCase();
    return this.filteredDataSources.filter(ds => 
      ds.dataSourceName?.toLowerCase().includes(query)
    );
  }
  
  /**
   * Get filtered datasources for Anomaly Detection based on search query
   */
  get filteredAnomalyDataSources(): DataSourceGet[] {
    if (!this.anomalyDataSourceSearchQuery.trim()) {
      return this.filteredDataSources;
    }
    const query = this.anomalyDataSourceSearchQuery.toLowerCase();
    return this.filteredDataSources.filter(ds => 
      ds.dataSourceName?.toLowerCase().includes(query)
    );
  }
  
  async onCleanDataFileUpload(): Promise<void> {
    this.uploadingClean = true;
    try {
      const fileData = await this.fileUploadService.selectFileForUpload(['.json', '.csv']);
      
      if (fileData) {
        this.cleanDataUploadedFile = {
          name: fileData.fileName,
          data: {
            fileName: fileData.fileName,
            fileContent: fileData.fileContent
          }
        };
      } else {
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to process file';
      this.snackBar.open(
        `Error: ${errorMessage}`, 
        '×', 
        {
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: 'error-snackbar'
        }
      );
    } finally {
      this.uploadingClean = false;
    }
  }
  
  onCleanDataFileRemove(): void {
    this.cleanDataUploadedFile = null;
  }

  async onAnomalyFileUpload(): Promise<void> {
    this.uploadingAnomaly = true;
    try {
      const uploadedData = await this.fileUploadService.openFileDialog(['.json', '.csv']);
      
      if (uploadedData) {
        this.anomalyDetectionUploadedFile = {
          name: uploadedData.fileName,
          data: uploadedData.data // Pass the parsed array of objects (matching hypothesis testing)
        };
        this.anomalyDetectionInput = uploadedData.fileName;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to process file';
      this.snackBar.open(
        `Error: ${errorMessage}`, 
        '×', 
        {
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: 'error-snackbar'
        }
      );
    } finally {
      this.uploadingAnomaly = false;
    }
  }

  onAnomalyFileRemove(): void {
    this.anomalyDetectionUploadedFile = null;
  }

  isAnomalyDetectionValid(): boolean {
    return this.anomalyDetectionUploadedFile !== null;
  }

  async onAnomalyDetectionGo(): Promise<void> {
    if (!this.isAnomalyDetectionValid()) {
      return;
    }

    this.isCreating = true;
    this.creatingMessage = 'Preparing anomaly detection...';

    // Navigate to anomaly detection screen with data
    const navigationState: any = {};
    
    if (this.selectedAnomalyDataSource) {
      // Using existing datasource
      navigationState.dataSourceId = this.selectedAnomalyDataSource.dataSourceId;
      navigationState.dataSourceName = this.selectedAnomalyDataSource.dataSourceName;
    } else if (this.anomalyDetectionUploadedFile) {
      // Using uploaded file - pass the full object with metadata
      navigationState.uploadedFileData = this.anomalyDetectionUploadedFile;
    }

    // Always generate a default name with timestamp
    navigationState.name = this.generateAnomalyDetectionName();

    this.router.navigate(['/anomaly-detection'], {
      state: navigationState
    });
  }

  async onHypothesisTestingFileUpload(): Promise<void> {
    this.uploadingHypothesis = true;
    try {
      const uploadedData = await this.fileUploadService.openFileDialog(['.json', '.csv']);
      
      if (uploadedData) {
        this.hypothesisTestingUploadedFile = {
          name: uploadedData.fileName,
          data: uploadedData.data // Already parsed array of objects
        };
        this.hypothesisTestingInput = uploadedData.fileName;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to process file';
      this.snackBar.open(
        `Error: ${errorMessage}`, 
        '×', 
        {
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: 'error-snackbar'
        }
      );
    } finally {
      this.uploadingHypothesis = false;
    }
  }

  onHypothesisTestingFileRemove(): void {
    this.hypothesisTestingUploadedFile = null;
  }

  isHypothesisTestingValid(): boolean {
    return this.hypothesisTestingUploadedFile !== null;
  }

  onHypothesisTestingGo(): void {
    if (!this.isHypothesisTestingValid()) {
      return;
    }

    this.isCreating = true;
    this.creatingMessage = 'Preparing hypothesis testing...';

    // Navigate to hypothesis testing screen with data
    const navigationState: any = {};
    
    if (this.hypothesisTestingUploadedFile) {
      // Using uploaded file - pass the full object with metadata
      navigationState.uploadedFileData = this.hypothesisTestingUploadedFile;
    }

    this.router.navigate(['/hypothesis-testing'], {
      state: navigationState
    });
  }

  onMoreOptionsClick(): void {
    this.showCleanDataSourceMenu = false;
    this.showCleanDataSourceList = false;
    this.moreOptionsSelected = true;
    this.cleanDataUploadedFile = null; // Clear any uploaded file when More Options is selected
  }

  toggleRecentActivity(): void {
    this.isRecentActivityExpanded = !this.isRecentActivityExpanded;
    
    // Persist the drawer state in user settings
    const currentSettings = this.authService.getUserSettings();
    const updatedSettings = {
      ...currentSettings,
      userMode: currentSettings?.userMode || 0, // Preserve existing userMode
      dashboardActivityExpanded: this.isRecentActivityExpanded
    };
    
    this.dataService.updateUserSettings(updatedSettings).subscribe({
      next: () => {
        // Update local auth service
        this.authService.setUserSettings(updatedSettings);
      },
      error: (error) => {
        console.error('Failed to update dashboard activity state:', error);
        // Still update local state even if API call fails
        this.authService.setUserSettings(updatedSettings);
      }
    });
  }

  onHelpClick(topic: string): void {
    this.dialogService.openHelpDialog(topic);
  }

  openHelpWithFeature(feature: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.dialogService.openHelpDialog(undefined, feature);
  }

}
